import { Select, SelectProps } from "antd";
import React, { useEffect, useRef, useState } from "react";
import { Control, useController, useWatch } from "react-hook-form";
import { groupBy } from "../../../../Tools/ProcessingTools";

import "../styles.css";
import "./styles.css";

interface IMultSelectProps<T> extends SelectProps<string | number> {
    controller?: (fatherName?: any) => Promise<Array<T>>;
    data?: T[];

    getKeyValue: (option: T) => string | number | undefined;
    getDS: (option: T) => string | undefined;

    keyValue?: keyof T;
    DS?: keyof T;

    placeholder?: string;
    fatherName?: string;
    watch?: (fatherName: string) => any;

    // para compatibilidade com react-hook-form
    name?: string;
    control?: Control;
    defaultValue?: any;
    fatherDefaultValue?: any;
    virtual?: boolean;
}

export default function MultSelect<T>(props: IMultSelectProps<T>) {
    const {
        controller,
        name,
        control,
        getDS,
        getKeyValue,
        watch,
        fatherName,
        fatherDefaultValue,
        keyValue,
        DS,
        ...selectProps
    } = props;

    const [options, setOptions] = useState(props.data);
    const { field } = useController({
        name: name ?? "",
        control,
        defaultValue: props.defaultValue ?? [],
    });
    const fatherWatcher = useWatch({
        name: fatherName ?? "fakenamems",
        control,
        defaultValue: props.fatherDefaultValue ?? (fatherName ? [] : ""),
    });

    const memory = useRef(new Map<number, Array<T>>());

    useEffect(() => {
        if (!fatherName || fatherWatcher) {
            if (fatherWatcher && Array.isArray(fatherWatcher)) {
                // dependência de um select múltiplo
                // faz a chamada para todos os valores ( ou pega do memory )
                Promise.all(
                    fatherWatcher.map(async (value) =>
                        !memory.current.has(value)
                            ? controller?.(value).then((list) => {
                                  memory.current.set(value, list);
                                  return list;
                              })
                            : (async () => memory.current.get(value))()
                    )
                ).then((array: Array<any>) => {
                    if (array && DS && keyValue) {
                        //agrupa por DS e transforma em array de [DS, objeto]
                        const options = Array.from(
                            groupBy(array.flat(), (t) => t[DS]).entries()
                        );

                        // gera objeto que como options
                        setOptions(
                            options.map(([key, value]) => {
                                return {
                                    [keyValue]:
                                        value
                                            ?.map((v) => v[keyValue])
                                            ?.join(",") ?? "",
                                    [DS]: key,
                                } as unknown as T;
                            })
                        );
                    }
                });
            } else controller?.().then(setOptions);
        }
    }, [fatherWatcher, controller, fatherName, DS, keyValue]);

    return (
        <div>
            <Select
                mode="multiple"
                allowClear
                style={{ width: "100%" }}
                virtual={props.virtual}
                className="ant-select-custom-input"
                notFoundContent={
                    <div className="text-muted text-center">Nenhuma opção</div>
                }
                {...selectProps}
                {...field}
            >
                {options?.map((option) => (
                    <Select.Option
                        value={getKeyValue(option) ?? 0}
                        key={getKeyValue(option)}
                    >
                        {getDS(option)}
                    </Select.Option>
                ))}
            </Select>
        </div>
    );
}
